import { useMemo } from 'react';
import { 
  STANDARD_TAGS, 
  normalizeTagForStorage, 
  formatTagForDisplay,
  migrateLegacyTags
} from '../utils/tagUtils';

// Cache for processed filter options to avoid recalculation
const filterCache = new Map();

/**
 * Hook to extract dynamic filter options from forum questions dataset
 * @param {Array} questions - Array of question objects
 * @param {string} currentUserId - Current user ID for "My Posts" filter
 * @returns {Object} Filter options with counts and loading/error states
 */
const useDynamicForumFilters = (questions = [], currentUserId = null) => {
  const filterOptions = useMemo(() => {
    if (!questions || questions.length === 0) {
      return {
        availableTags: [],
        loading: false,
        error: null
      };
    }

    // Create cache key based on questions data and user ID
    const cacheKey = JSON.stringify({
      questions: questions.map(q => ({
        id: q.id,
        tags: q.tags,
        authorId: q.authorId
      })),
      currentUserId
    });

    // Check cache first
    if (filterCache.has(cacheKey)) {
      return filterCache.get(cacheKey);
    }

    try {
      const availableFilters = [];
      
      // Add "My Posts" filter if user has posts
      if (currentUserId) {
        const userPostCount = questions.filter(q => q.authorId === currentUserId).length;
        if (userPostCount > 0) {
          availableFilters.push({
            value: 'my-posts',
            label: 'My Posts',
            count: userPostCount,
            type: 'user'
          });
        }
      }

      // Extract unique tags from all questions, but only include standard tags
      const tagMap = new Map();

      questions.forEach(question => {
        if (question.tags && Array.isArray(question.tags)) {
          question.tags.forEach(tag => {
            if (tag && tag.trim()) {
              // Check if this tag is one of our standard tags (in Title Case format)
              const standardTagLabels = Object.values(STANDARD_TAGS);
              if (standardTagLabels.includes(tag.trim())) {
                tagMap.set(tag.trim(), (tagMap.get(tag.trim()) || 0) + 1);
              }
            }
          });
        }
      });

      // Convert map to sorted array with proper formatting
      const tagFilters = Array.from(tagMap.entries())
        .map(([titleCaseTag, count]) => {
          // Convert Title Case to kebab-case for the value, keep Title Case for label
          const kebabCaseValue = normalizeTagForStorage(titleCaseTag);
          return {
            value: kebabCaseValue,
            label: titleCaseTag, // Keep the original Title Case format
            count,
            type: 'tag'
          };
        })
        .sort((a, b) => {
          // Sort by popularity first, then alphabetically
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          return a.label.localeCompare(b.label);
        });

      // Add tag filters to available filters
      availableFilters.push(...tagFilters);

      const result = {
        availableTags: availableFilters,
        loading: false,
        error: null
      };

      // Cache the result
      filterCache.set(cacheKey, result);
      
      // Limit cache size to prevent memory leaks
      if (filterCache.size > 10) {
        const firstKey = filterCache.keys().next().value;
        filterCache.delete(firstKey);
      }

      return result;
    } catch (error) {
      console.error('Error processing forum filters:', error);
      return {
        availableTags: [],
        loading: false,
        error: error.message || 'Failed to process filter options'
      };
    }
  }, [questions, currentUserId]);

  return filterOptions;
};



export default useDynamicForumFilters;